import React, {useCallback, useEffect, useState} from 'react';
import {Box} from 'src/design/atoms/Box';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import type {
  NotificationAttributeEnum,
  SourceEntityRecord,
  SourceEntityRequest,
  WorkflowFormFieldPersonnelTarget,
} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  TaskTypeEnum,
  WorkflowDefinitionTriggerTypeEnum,
} from 'src/api/ProactApiClient';
import {SelectableCard} from 'src/design/molecules/SelectableCard';
import {CheckboxList} from 'src/design/atoms/CheckboxList';
import {HelpText} from 'src/design/atoms/HelpText';
import {Switch} from 'src/design/atoms/Switch';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';
import {isValidArray} from 'src/utilities/objectUtils';
import type {IFeatureFlagSelectOption} from 'src/types/Form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {ActionForm, useActionForm} from 'src/app/actions/ActionForm';
import {ActionFormMetaRule} from 'src/app/actions/actionHelpers';
import {TaskFeatureEditor} from 'src/app/tasks/TaskFeatureEditor';
import {CONTENT_PDF_FIELD_NAME} from 'src/app/content-pdf/contentPdf';
import {FormFieldPersonnelTargetEditor} from 'src/app/workflows/FormFieldPersonnelTargetEditor';
import {WorkflowTargetAttributes} from 'src/app/workflows/workflowHelpers';
import {WorkflowNotificationAttributeSection} from 'src/app/workflows/WorkflowNotificationAttributeSection';
import type {ActionEditorProps} from './ActionCard';
import {SectionLabel} from './SectionLabel';

const taskTypeConfigs: (IFeatureFlagSelectOption & {description: string})[] = [
  {
    id: TaskTypeEnum.Actions,
    label: 'Action',
    featureFlag: FeatureFlagEnum.Actions,
    description: 'Create a standard action task for the assigned user.',
  },
  {
    id: TaskTypeEnum.Approval,
    label: 'Approval',
    featureFlag: FeatureFlagEnum.Approvals,
    description:
      'Create an approval request that must be accepted or rejected.',
  },
  {
    id: TaskTypeEnum.ToolboxTalk,
    label: 'Toolbox Talk',
    featureFlag: FeatureFlagEnum.ToolboxTalks,
    description: 'Create a toolbox talk for a group safety discussion.',
  },
  {
    id: TaskTypeEnum.Form,
    label: 'Form',
    featureFlag: FeatureFlagEnum.Forms,
    description: 'Create a form submission task for the assigned user.',
  },
];

interface WorkflowTaskFieldsProps {
  formData: IFormDataV2;
  transformMeta: (fields: IFormFieldV2[]) => IFormFieldV2[];
  onValuesChange: (values: IFormDataV2) => void;
}

const WorkflowActionFields: React.FC<WorkflowTaskFieldsProps> = ({
  formData,
  transformMeta,
  onValuesChange,
}) => {
  const {formApi, isLoading} = useActionForm({
    metarules: [ActionFormMetaRule.UseDueInDays],
    transformMeta,
    initialData: toFormDataV2(formData),
  });
  return (
    <ActionForm
      form={formApi}
      isLoading={isLoading}
      onValuesChange={onValuesChange}
    />
  );
};

const WorkflowFeatureTaskFields: React.FC<
  WorkflowTaskFieldsProps & {taskType: TaskTypeEnum}
> = ({taskType, formData, transformMeta, onValuesChange}) => (
  <TaskFeatureEditor
    taskType={taskType}
    transformMeta={transformMeta}
    initialData={toFormDataV2(formData)}
    onValuesChange={onValuesChange}>
    {({form}) => form}
  </TaskFeatureEditor>
);

export const ActionCreateTaskEditor: React.FC<ActionEditorProps> = ({
  action,
  onActionChange,
  metadata,
}) => {
  const {isScheduled} = metadata;
  const activeFeatureFlags = useActiveFeatureFlags();
  const [activeTaskTypes, setActiveTaskTypes] = useState(taskTypeConfigs);

  const taskType = action.metadata?.createTaskMetadata?.taskType;
  const isActionTaskType = taskType === TaskTypeEnum.Actions;

  const dueInDays = action.metadata?.createTaskMetadata?.dueInDays;
  let dueAt = '';
  if (!isNaN(Number(dueInDays))) {
    const date = new Date();
    date.setDate(date.getDate() + Number(dueInDays));
    dueAt = date.toString().toShortDate();
  }

  const formData = {
    ...action.metadata?.createTaskMetadata,
    sourceEntities:
      action.metadata?.createTaskMetadata?.targetSourceEntityRequest,
    dueAt: dueAt,
    complianceStandardIds:
      action.metadata?.createTaskMetadata?.targetComplianceStandardIds ?? [],
  };

  const handleBuildTaskMeta = useCallback(
    (fields: IFormFieldV2[]): IFormFieldV2[] => {
      return fields
        .filter(
          field =>
            field.name !== 'previewDueAt' &&
            field.name !== CONTENT_PDF_FIELD_NAME
        )
        .map(field => {
          if (field.name === 'subject') {
            return {
              ...field,
              description:
                'Variables can be used to add dynamic data to this field. Refer to the variables lists for more information.',
            };
          }
          if (field.name === 'content') {
            return {
              ...field,
              description:
                'This field will automatically have metadata appended to it when the workflow executes. This includes information about how it was triggered and links to related items. ' +
                'Variables can be used to add dynamic data to this field. Refer to the variables lists for more information.',
            };
          }
          return field;
        });
    },
    []
  );

  useEffect(() => {
    const active = taskTypeConfigs.filter(
      t => !t.featureFlag || activeFeatureFlags.includes(t.featureFlag)
    );
    setActiveTaskTypes(active);
  }, [activeFeatureFlags]);

  const handleTaskTypeChange = (value: string) => {
    onActionChange({
      ...action,
      metadata: {
        ...action.metadata,
        createTaskMetadata: {
          ...action.metadata?.createTaskMetadata,
          taskType: Number(value) as TaskTypeEnum,
        },
      },
    });
  };

  const handleFormDataChange = (data: IFormDataV2) => {
    const targetSourceEntities = isValidArray(data?.sourceEntities?.entities)
      ? data.sourceEntities.entities
          .map((item: any) => {
            if (item.entityType) {
              return {
                entityType: item.entityType,
                entityId: item.entityId,
              } as SourceEntityRecord;
            }
            return undefined;
          })
          .filter((item: any) => item !== undefined)
      : [];

    const targetSourceEntityRequest = {
      entities: targetSourceEntities,
      selectAllEntitiesType: data?.sourceEntities?.selectAllEntitiesType,
    } as SourceEntityRequest;

    const targetComplianceStandardIds = isValidArray(data.complianceStandardIds)
      ? data?.complianceStandardIds?.map((e: any) => e.id)
      : [];

    onActionChange({
      ...action,
      metadata: {
        ...action.metadata,
        createTaskMetadata: {
          ...action.metadata?.createTaskMetadata,
          completionType: data.completionType,
          approvalFlowType: data.approvalFlowType,
          subject: data.subject,
          content: data.content,
          dueInDays: data.dueInDays,
          refresh: data.refresh,
          formDefinitionId: data.formDefinitionId,
          targetComplianceStandardIds: targetComplianceStandardIds,
          targetSourceEntityRequest: {
            ...action.metadata?.createTaskMetadata?.targetSourceEntityRequest,
            ...targetSourceEntityRequest,
          },
        },
      },
    });
  };

  const handleStopWorkflowOnApprovalRejectionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onActionChange({
      ...action,
      metadata: {
        ...action.metadata,
        createTaskMetadata: {
          ...action.metadata?.createTaskMetadata,
          stopWorkflowOnApprovalRejection: e.currentTarget.checked,
        },
      },
    });
  };

  const handleFilterByTriggererPrimaryProjectChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onActionChange({
      ...action,
      metadata: {
        ...action.metadata,
        createTaskMetadata: {
          ...action.metadata?.createTaskMetadata,
          filterByTriggererPrimaryProject: e.currentTarget.checked,
        },
      },
    });
  };

  const handleTargetingAttributesChange = (statusValues: string[]) => {
    onActionChange({
      ...action,
      metadata: {
        ...action.metadata,
        createTaskMetadata: {
          ...action.metadata?.createTaskMetadata,
          targetSourceEntityRequest: {
            ...action.metadata?.createTaskMetadata?.targetSourceEntityRequest,
            entities:
              action.metadata?.createTaskMetadata?.targetSourceEntityRequest
                ?.entities ?? [],
            targetingAttributeSourceEntityId: undefined,
            targetingAttributes: statusValues.map(
              s => Number(s) as NotificationAttributeEnum
            ),
          },
        },
      },
    });
  };

  const isFormTrigger =
    metadata.primaryTrigger?.type ===
      WorkflowDefinitionTriggerTypeEnum.FormSubmission ||
    metadata.primaryTrigger?.type ===
      WorkflowDefinitionTriggerTypeEnum.FormFieldValueSubmission;

  const handleFormFieldPersonnelTargetsChange = (
    targets: WorkflowFormFieldPersonnelTarget[]
  ) => {
    onActionChange({
      ...action,
      metadata: {
        ...action.metadata,
        createTaskMetadata: {
          ...action.metadata?.createTaskMetadata,
          formFieldPersonnelTargets: targets,
        },
      },
    });
  };

  return (
    <Stack gap="md">
      <SectionLabel label="Task Type" withSeparation={false} />
      <SimpleGrid cols={{base: 1, sm: 2}}>
        {activeTaskTypes.map(c => (
          <SelectableCard
            key={Number(c.id)}
            label={c.label}
            description={c.description}
            value={String(c.id)}
            selected={taskType === Number(c.id)}
            onClick={handleTaskTypeChange}
          />
        ))}
      </SimpleGrid>

      {taskType === TaskTypeEnum.Approval && (
        <Switch
          label="Stop Workflow on Rejection"
          description="If enabled, the rejection of this approval will automatically cancel the remaining workflow. Otherwise, completion or rejection will both continue the workflow."
          checked={
            action.metadata?.createTaskMetadata
              ?.stopWorkflowOnApprovalRejection ?? false
          }
          onChange={handleStopWorkflowOnApprovalRejectionChange}
        />
      )}

      <SectionLabel label="Targeting" />

      <Box>
        <Switch
          label="Limit to triggerer's primary project"
          description="Only users whose primary project matches the primary project of the user who triggered this workflow are assigned. If that user has no primary project, nobody is assigned."
          disabled={isScheduled}
          checked={
            action.metadata?.createTaskMetadata
              ?.filterByTriggererPrimaryProject ?? false
          }
          onChange={handleFilterByTriggererPrimaryProjectChange}
        />
        {isScheduled && (
          <HelpText text="This field cannot be used with a Scheduled trigger. Please choose another trigger type to use this property." />
        )}
      </Box>

      <Box>
        <CheckboxList
          label="Assign by Target Attributes"
          disabled={isScheduled}
          values={WorkflowTargetAttributes}
          selected={
            action.metadata?.createTaskMetadata?.targetSourceEntityRequest?.targetingAttributes?.map(
              t => String(t)
            ) ?? []
          }
          onChange={handleTargetingAttributesChange}
        />
        {isScheduled && (
          <HelpText text="This field cannot be used with a Scheduled trigger. Please choose another trigger type to use this property." />
        )}
      </Box>

      {isFormTrigger && (
        <FormFieldPersonnelTargetEditor
          targets={
            action.metadata?.createTaskMetadata?.formFieldPersonnelTargets ?? []
          }
          formDefinitionId={
            metadata.primaryTrigger?.triggerFormDefinitionId ?? undefined
          }
          onTargetsChange={handleFormFieldPersonnelTargetsChange}
        />
      )}

      {taskType !== undefined && taskType !== TaskTypeEnum.Invalid && (
        <>
          <SectionLabel label="Task Details" />
          {isActionTaskType ? (
            <WorkflowActionFields
              key={taskType}
              formData={formData}
              transformMeta={handleBuildTaskMeta}
              onValuesChange={handleFormDataChange}
            />
          ) : (
            <WorkflowFeatureTaskFields
              key={taskType}
              taskType={taskType}
              formData={formData}
              transformMeta={handleBuildTaskMeta}
              onValuesChange={handleFormDataChange}
            />
          )}
        </>
      )}

      <WorkflowNotificationAttributeSection
        action={action}
        metadata={metadata}
      />
    </Stack>
  );
};
